import React from 'react'
import Flex from 'typescript-styled-flex'
import { useTranslation } from 'react-i18next'
import {
  getFormValues,
  InjectedFormProps,
  isSubmitting,
  reduxForm,
} from 'redux-form'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import requestTransferReservation from '../../../actions/bookingCalendar/event/transferReservation'
import { bindToReduxForm } from '../../../actions/form'
import { Form, FormGroup, Select } from '../../../components/FormBuilder'
import DateRangeContainerWithDateFields from '../../../components/FormBuilder/DateRangeInput/DateRangeContainerWithDateFields'
import { Desktop, is, Mobile } from '../../../components/MediaQuery'
import { IEvent } from '../../../models/Event'
import { IRoom } from '../../../models/Room'
import mapRoomTypeToIcon from '../../../models/Room/mappers/mapRoomTypeToIcon'
import { IStore } from '../../../reducers'
import { ICreateReservationFormValues } from '../../Form/CreateReservation/CreateReservation'
import Button from '../../../components/Template/Button'
import { fireToastError } from '../../../actions/toasts/fireToast'
import SpinningIcon from '../../../components/Template/SpinningIcon'
import { getRoomsAvailableWithinDateRange } from '../../../reducers/bookingCalendar/selectors/rooms'
import useSize from '../../../hooks/useSize'

function TransferForm({
  transfer,
  fireToast,
  formValues,
  rooms,
  isLoading,
  handleSubmit,
}: ITransferFormProps &
  ITransferFormState &
  ITransferFormDispatch &
  InjectedFormProps) {
  const [t] = useTranslation()
  const isDesktop = is.desktop()
  const [error, setError] = React.useState(false)
  const [disabled, setDisabled] = React.useState(true)
  const size = useSize()

  /* eslint-disable */
  React.useEffect(() => {
    setDisabled(!(formValues && formValues.roomId))
  })

  function normalizeFromDate(value, prev, all) {
    if (value >= all.dateTo) {
      fireToast(t('reservation_error_end_date_before_start_date'))
      setError(true)
      return prev
    } else {
      setError(false)
      return value
    }
  }

  function normalizeToDate(value, prev, all) {
    if (value <= all.dateFrom) {
      fireToast(t('reservation_error_end_date_before_start_date'))
      setError(true)
      return prev
    } else {
      setError(false)
      return value
    }
  }

  return (
    <Form onSubmit={handleSubmit(transfer)}>
      <Flex column={true} style={{ paddingRight: '0', paddingTop: '0' }}>
        <Flex style={{ width: '100%' }} row={true}>
          <DateRangeContainerWithDateFields containerStyles={{ zIndex: 5000 }}>
            {({ DateFromInput, DateToInput }) => (
              <>
                <DateFromInput
                  size={size}
                  readonly={true}
                  name={'dateFrom'}
                  sized={true}
                  disableMobileResizing={true}
                  isCenterOnMobile={!isDesktop}
                  normalize={normalizeFromDate}
                  errorMsg={error}
                  label={t('reservation_from')}
                  width={'100%'}
                  changeDateOnDisable={true}
                />
                <DateToInput
                  size={size}
                  readonly={true}
                  isCenterOnMobile={!isDesktop}
                  wrapperStyle={{ marginLeft: `${20 * size}px` }}
                  name={'dateTo'}
                  normalize={normalizeToDate}
                  errorMsg={error}
                  sized={true}
                  disableMobileResizing={true}
                  label={t('reservation_to')}
                  width={'100%'}
                  changeDateOnDisable={true}
                />
              </>
            )}
          </DateRangeContainerWithDateFields>
        </Flex>

        <FormGroup.Render
          size={size}
          styleGroup={{ paddingBottom: '0' }}
          column={true}
          title={t('accommodation')}
        >
          <Flex
            style={{ marginTop: `${15 * size}px`, width: '100%' }}
            row={true}
          >
            <Desktop>
              <Select.ReduxField
                size={size}
                readonly={true}
                placeholder={t('select')}
                width={'100%'}
                noOptionsMessage={t('search_not_found')}
                defaultOpen={true}
                menuMaxHeight={'calc(50vh - 63px)'}
                menuMaxWidth={'100%'}
                staticMenu={true}
                name={'roomId'}
                resetOnSelectedOptionMissing={true}
              >
                {rooms &&
                  rooms.map(r => (
                    <Select.Option
                      key={`select-transfer-${r.id}`}
                      icon={mapRoomTypeToIcon(r)}
                      value={r.id}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {r.name}
                      </span>
                    </Select.Option>
                  ))}
              </Select.ReduxField>
            </Desktop>
            <Mobile>
              <Select.ReduxField
                size={size}
                readonly={true}
                placeholder={t('select')}
                width={'450px'}
                noOptionsMessage={t('search_not_found')}
                menuMaxHeight={'35vh'}
                staticMenu={true}
                name={'roomId'}
                resetOnSelectedOptionMissing={true}
              >
                {rooms &&
                  rooms.map(r => (
                    <Select.Option
                      key={`select-transfer-${r.id}`}
                      icon={mapRoomTypeToIcon(r)}
                      value={r.id}
                    >
                      {r.name}
                    </Select.Option>
                  ))}
              </Select.ReduxField>
            </Mobile>
          </Flex>
        </FormGroup.Render>
        <Button
          key={formValues ? formValues.roomId : undefined}
          size={size}
          as={'button'}
          style={{ marginTop: `${10 * size}px`, width: '100%' }}
          primary={!isLoading}
          disabled={disabled || error || isLoading}
        >
          {isLoading ? (
            <SpinningIcon size={16 * size + 'px'} />
          ) : (
            t('reservation_move_btn')
          )}
        </Button>
      </Flex>
    </Form>
  )
}

const mapStateToProps = (state: IStore, props: ITransferFormProps) => {
  const formValues: any = getFormValues('transferForm')(state)
  const values = props.eventData

  return {
    formValues,
    isLoading: isSubmitting('transferForm')(state),
    rooms: formValues
      ? getRoomsAvailableWithinDateRange(state, {
          dateTo: formValues.dateTo,
          dateFrom: formValues.dateFrom,
        })
      : [],
    initialValues: {
      dateFrom: values ? values.dateFrom : null,
      dateTo: values && values.dateTo ? values.dateTo : null,
      room: [],
      eventData: props.eventData,
      eventId: props.eventId,
    },
  }
}

const mapDispatchToProps = (dispatch: Dispatch): ITransferFormDispatch => ({
  transfer: bindToReduxForm(requestTransferReservation),
  fireToast: (message) =>
    dispatch(fireToastError(message, 5000, 'Main', 'transferDateError')),
})

const ReduxForm = reduxForm<
  ITransferFormValues,
  ITransferFormProps & ITransferFormState & ITransferFormDispatch
>({
  form: 'transferForm',
  destroyOnUnmount: true,
  enableReinitialize: true,
  onSubmitSuccess: (result, dispatch, props) => {
    props.onSuccess()
  },
})(TransferForm)

const TransferReservationForm = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ReduxForm)
export default TransferReservationForm

interface ITransferFormProps {
  eventData: ICreateReservationFormValues
  eventId: IEvent['id']
  onSuccess: () => void
}

interface ITransferFormState {
  formValues: ITransferFormValues
  rooms: IRoom[]
  isLoading: boolean
}

interface ITransferFormDispatch {
  fireToast: (message: string) => void
  transfer: (values: ITransferFormValues, dispatch: Dispatch) => any
}

export interface ITransferFormValues {
  dateFrom: Date
  dateTo: Date | null
  roomId: IRoom['id']
  eventData: ICreateReservationFormValues
  eventId: IEvent['id']
}
